/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../../shared/theme/ticketColors';
import { PaperSheet } from '../../../shared/ticket/PaperSheet';
import { showError } from '../../../shared/ui/appToast';
import { settingsStyles as styles } from '../settingsScreen.styles';
import type { ScheduledClosure } from '../../domain/storeProfileTypes';
import { LocalDateTimeField } from './LocalDateTimeField';

type Props = {
  closures: ScheduledClosure[];
  loading: boolean;
  canEdit: boolean;
  /** Layout dos columnas en formulario (desktop). */
  wide?: boolean;
  onCreate: (startDate: string, endDate: string, reason?: string) => void;
  onDelete: (id: string) => void;
};

function defaultStart(): Date {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  return d;
}

function defaultEnd(): Date {
  const d = defaultStart();
  d.setDate(d.getDate() + 1);
  return d;
}

function formatClosureDate(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false })
  );
}

export function ScheduledClosuresSection({
  closures,
  loading,
  canEdit,
  wide,
  onCreate,
  onDelete,
}: Props) {
  const [showForm, setShowForm] = useState(false);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [reason, setReason] = useState('');

  const handleSubmit = () => {
    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();
    if (endISO <= startISO) {
      showError('Rango inválido', 'La fecha de fin tiene que ser después del inicio.');
      return;
    }
    if (startISO < new Date().toISOString()) {
      showError('Fecha pasada', 'Elegí una fecha de inicio futura.');
      return;
    }
    onCreate(startISO, endISO, reason.trim() || undefined);
    setShowForm(false);
    setReason('');
    setStartDate(defaultStart());
    setEndDate(defaultEnd());
  };

  return (
    <PaperSheet padded>
      <Text style={styles.cardTitle}>Cierres programados</Text>
      <Text style={styles.captionBlock}>
        En esas fechas la tienda se ve cerrada.
      </Text>

      {loading ? (
        <ActivityIndicator color={ticketColors.ink2} />
      ) : closures.length === 0 ? (
        <Text style={styles.caption}>Sin cierres programados.</Text>
      ) : (
        <View style={{ gap: spacing.xs }}>
          {closures.map((c) => (
            <View key={c.id} style={local.closureRow}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.captionStrong}>
                  {formatClosureDate(c.startDate)} — {formatClosureDate(c.endDate)}
                </Text>
                {c.reason ? <Text style={styles.caption}>{c.reason}</Text> : null}
              </View>
              {canEdit ? (
                <TouchableOpacity
                  style={local.ghostBtn}
                  onPress={() => onDelete(c.id)}
                  hitSlop={8}
                  accessibilityRole="button"
                >
                  <Text style={local.ghostBtnText}>Eliminar</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          ))}
        </View>
      )}

      {canEdit && showForm ? (
        <View style={local.formBlock}>
          <View style={wide ? local.twoCol : local.formStack}>
            <View style={wide ? local.twoColItem : undefined}>
              <LocalDateTimeField label="Desde" value={startDate} onChange={setStartDate} />
            </View>
            <View style={wide ? local.twoColItem : undefined}>
              <LocalDateTimeField label="Hasta" value={endDate} onChange={setEndDate} />
            </View>
          </View>
          <View style={wide ? local.reasonWide : undefined}>
            <Text style={styles.captionStrong}>Motivo (opcional)</Text>
            <TextInput
              style={styles.input}
              value={reason}
              onChangeText={setReason}
              placeholder="Ej. Feriado, inventario…"
              placeholderTextColor={ticketColors.ink3}
              editable
            />
          </View>
          <View style={local.formActions}>
            <TouchableOpacity
              style={local.compactBtn}
              onPress={() => setShowForm(false)}
              accessibilityRole="button"
            >
              <Text style={local.compactBtnText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[local.compactBtn, local.compactBtnPrimary]}
              onPress={handleSubmit}
              accessibilityRole="button"
            >
              <Text style={[local.compactBtnText, local.compactBtnTextPrimary]}>Agregar cierre</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : canEdit ? (
        <TouchableOpacity
          style={local.compactBtn}
          onPress={() => setShowForm(true)}
          accessibilityRole="button"
        >
          <Text style={local.compactBtnText}>+ Programar cierre</Text>
        </TouchableOpacity>
      ) : null}
    </PaperSheet>
  );
}

const local = StyleSheet.create({
  wideCard: {
    width: '100%',
    alignSelf: 'stretch',
  },
  closureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    padding: spacing.sm,
  },
  ghostBtn: {
    alignSelf: 'flex-start',
    paddingVertical: 6,
    paddingHorizontal: 10,
    minHeight: 32,
    justifyContent: 'center',
  },
  ghostBtnText: {
    fontSize: 12,
    fontFamily: ticketFonts.sans,
    fontWeight: '600',
    color: ticketColors.ink2,
  },
  formBlock: {
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  formStack: {
    gap: spacing.sm,
  },
  twoCol: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  twoColItem: {
    flexGrow: 1,
    flexBasis: 200,
    minWidth: 180,
  },
  reasonWide: {
    maxWidth: 420,
    width: '100%',
  },
  formActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: spacing.xs,
  },
  compactBtn: {
    alignSelf: 'flex-start',
    backgroundColor: ticketColors.paper2,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    paddingVertical: 0,
    paddingHorizontal: 10,
    minHeight: 32,
    justifyContent: 'center',
  },
  compactBtnPrimary: {
    backgroundColor: ticketColors.gold,
    borderColor: ticketColors.gold,
  },
  compactBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  compactBtnTextPrimary: {
    color: ticketColors.goldInk,
  },
});
